import { Box, Stack, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState, useEffect } from 'react';
import { useBillsQuery } from '../../api/getBills';

import { usePoliciesQuery } from '../../api/getPolicies';
import { useWelcomeQuery } from '../../api/getWelcomeDetails';
import { usePreferencesQuery } from '../../api/preferences/getPreferences';
import InfoCards from '../../components/InfoCards';
import NavBar from '../../components/NavBar';
import SubHeader from '../../components/SubHeader';
import colors from '../../constants/colors';
import BillCards from './BillCards';
import ClaimCards from './ClaimCards';
import PolicyCards from './PolicyCards';

const tabMap = {
  bills: 0,
  policies: 1,
  claims: 2,
};

interface OverviewPageProps {
  currentTab?: 'bills' | 'claims' | 'policies';
}

const OverviewPage = ({ currentTab = 'bills' }: OverviewPageProps) => {
  const [tabValue, setTabValue] = useState(tabMap[currentTab]);
  const { data: welcomeData } = useWelcomeQuery();
  const { data: policiesData } = usePoliciesQuery();
  const { data: billsData } = useBillsQuery();
  const bills = billsData?.payments;

  const authToken = welcomeData?.authToken ?? '';
  const crossSellInfo = policiesData?.crossSellInfo;
  const showAutoHomeCard = crossSellInfo?.crossSellEligible && !!bills;

  useEffect(() => {
    setTabValue(tabMap[currentTab]);
  }, [currentTab]);

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('desktop'));

  const customerHasUbiPolicy =
    !!policiesData?.ubiEnrollment?.safeDriveEnrolled ||
    !!policiesData?.ubiEnrollment?.smartTrekEnrolled;

  const activePolicies = policiesData?.policies.filter(
    (policy) => policy?.policyStatus === 'Active'
  );

  // Only use policies that are available in both the /welcome (CDH) and /policies (GPD) responses
  const verifiedPolicies = activePolicies?.filter((policy) =>
    welcomeData?.policies.find(
      (welcomePolicy) => welcomePolicy.policyNumber === policy.policyNumber
    )
  );
  const { data: preferenceData } = usePreferencesQuery(
    authToken,
    verifiedPolicies?.map((policy) => ({
      policyNumber: policy.policyNumber,
      policySourceSystem: policy.dataSource,
    }))
  );

  const isBothActiveAndUnenrolled = verifiedPolicies?.some((activePolicy) =>
    preferenceData?.selections?.some(
      (selection) =>
        selection?.policyNumber === activePolicy?.policyNumber &&
        (selection?.policyDocuments as string) !== 'E'
    )
  );

  return (
    <Box
      data-testid="overviewPage"
      sx={{
        display: 'flex',
        flex: 1,
        width: 1,
        flexDirection: 'column',
        minHeight: '100%',
        backgroundColor: colors.csaaGray,
        px: { mobile: 2, desktop: 7 },
        pb: 3,
      }}
    >
      <SubHeader />
      {isDesktop ? (
        <Stack direction="row" spacing={3} width={1}>
          <Stack spacing={3} width={1}>
            <NavBar value={tabValue} />
            {tabValue === 0 && <BillCards />}
            {tabValue === 1 && <PolicyCards />}
            {tabValue === 2 && <ClaimCards />}
          </Stack>
          <Stack
            spacing={3}
            data-testid="infoCardsContainer"
            sx={{ maxWidth: 358, width: 1 }}
          >
            <InfoCards
              tabValue={tabValue}
              ubiEnabled={customerHasUbiPolicy}
              unenrolledPaperlessPolicy={isBothActiveAndUnenrolled}
              showAutoHomeCard={showAutoHomeCard}
            />
          </Stack>
        </Stack>
      ) : (
        <Stack spacing={3} width={1}>
          <NavBar value={tabValue} />
          {tabValue === 0 && (
            <BillCards
              ubiEnabled={customerHasUbiPolicy}
              unenrolledPaperlessPolicy={isBothActiveAndUnenrolled}
            />
          )}
          {tabValue === 1 && (
            <PolicyCards
              ubiEnabled={customerHasUbiPolicy}
              unenrolledPaperlessPolicy={isBothActiveAndUnenrolled}
            />
          )}
          {tabValue === 2 && (
            <ClaimCards
              ubiEnabled={customerHasUbiPolicy}
              unenrolledPaperlessPolicy={isBothActiveAndUnenrolled}
            />
          )}
        </Stack>
      )}
    </Box>
  );
};

export default OverviewPage;
